import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { fetchCounties } from "./api";

const CountyContext = createContext(null);

const STORAGE_KEY = "okwatch:county";

export function CountyProvider({ children }) {
  const [counties, setCounties] = useState([]);
  const [county, setCountyState] = useState(() => {
    if (typeof window === "undefined") return "okanogan";
    try {
      const stored = localStorage.getItem(STORAGE_KEY) || "okanogan";
      return ["washington", "bcborder"].includes(stored) ? "okanogan" : stored;
    } catch (e) {
      console.warn("County: localStorage read failed", e);
      return "okanogan";
    }
  });

  useEffect(() => {
    // Retry with backoff — right after a deploy the backend may still be
    // starting up, and a single failed fetch left the dropdown missing.
    let alive = true;
    let attempt = 0;
    const load = () => {
      fetchCounties()
        .then((list) => {
          if (!alive) return;
          if (list && list.length) {
            setCounties(list);
          } else if (attempt < 5) {
            attempt += 1;
            setTimeout(load, 3000 * attempt);
          }
        })
        .catch((e) => {
          console.warn("County: fetch failed", e);
          if (alive && attempt < 5) {
            attempt += 1;
            setTimeout(load, 3000 * attempt);
          }
        });
    };
    load();
    return () => { alive = false; };
  }, []);

  const setCounty = useCallback((slug) => {
    setCountyState(slug);
    try {
      localStorage.setItem(STORAGE_KEY, slug);
      localStorage.setItem("okwatch:lastview", slug);
    } catch (e) {
      console.warn("County: localStorage write failed", e);
    }
  }, []);

  const current = useMemo(
    () => counties.find((c) => c.slug === county) || { slug: county, name: county },
    [counties, county],
  );

  const value = useMemo(
    () => ({ county, setCounty, counties, current }),
    [county, setCounty, counties, current],
  );

  return <CountyContext.Provider value={value}>{children}</CountyContext.Provider>;
}

export function useCounty() {
  const ctx = useContext(CountyContext);
  if (!ctx) {
    return { county: "okanogan", setCounty: () => {}, counties: [], current: { slug: "okanogan", name: "Okanogan" } };
  }
  return ctx;
}

const REGION_NAMES = { washington: "Washington State", bcborder: "BC Border" };

export function CountyOverride({ slug, children }) {
  const parent = useCounty();
  const value = useMemo(
    () => ({
      county: slug,
      setCounty: parent.setCounty,
      counties: parent.counties,
      current: parent.counties.find((c) => c.slug === slug) || { slug, name: REGION_NAMES[slug] || slug },
    }),
    [slug, parent],
  );
  return <CountyContext.Provider value={value}>{children}</CountyContext.Provider>;
}
